export const JOB_STATUSES = [
  "New",
  "Artwork Check",
  "Gang Sheet",
  "Queued for Print",
  "Printing",
  "Curing",
  "Quality Check",
  "Ready for Dispatch",
  "Completed",
  "On Hold",
] as const;

export type JobStatus = (typeof JOB_STATUSES)[number];

export interface Customer {
  id: string;
  company_name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  created_at: string;
  brand?: string | null;
}

export interface Job {
  id: string;
  customer_id: string;
  order_number?: number | null;
  job_title: string;
  product_type: string | null;
  print_size: string | null;
  quantity: number | null;
  colours: string | null;
  notes: string | null;
  status: JobStatus;
  due_date: string | null;
  created_at: string;
  updated_at: string;
  cutting_tier_id: string | null;
  shipping_method_id: string | null;
  gangsheet_design_id: string | null;
  gangsheet_preview_url?: string | null;
  gangsheet_saved_at?: string | null;
  gang_sheet_name?: string | null;
  gangsheet_confirmed_at?: string | null;
  gangsheet_confirmed_by?: string | null;
  archived_at?: string | null;
  payment_status: "pending" | "confirmed" | "invoiced";
  assignee_profile_id: string | null;
  drip_size_id: number | null;
  metres: number | null;
  quality_status: "ok" | "warned" | "poor" | null;
  customer_profile_id?: string | null;
  vat_amount?: number | null;
  shipping_cost?: number | null;
  total_cost?: number | null;
  shipping_address?: string | null;
  billing_address?: string | null;
  customer?: Customer | null;
  files?: JobFile[];
}

export interface GangSheetSize {
  id: string;
  drip_size_id: number;
  width: number;
  height: number;
  unit: string;
  metres_equivalent: number;
  product_code: string;
  active: boolean;
  created_at: string;
}

export interface JobFile {
  id: string;
  job_id: string;
  file_name: string;
  file_url: string;
  file_type?: "artwork" | "proof" | "gang_sheet" | "other";
  uploaded_at: string;
}

export interface JobActivity {
  id: string;
  job_id: string;
  event_type: string;
  message: string | null;
  created_at: string;
}

export interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  role: "customer" | "staff" | "admin";
  customer_id: string | null;
  avatar_url?: string | null;
  teams_enabled?: boolean;
  white_label_enabled?: boolean;
  white_label_name?: string | null;
  white_label_tagline?: string | null;
  white_label_logo_url?: string | null;
  white_label_primary_color?: string | null;
  white_label_accent_color?: string | null;
  brand?: string | null;
  currency?: string | null;
}

export const STATUS_TONES: Record<JobStatus, string> = {
  New: "bg-[oklch(0.95_0.05_245)] text-[oklch(0.4_0.18_245)]",
  "Artwork Check": "bg-[oklch(0.95_0.08_350)] text-[oklch(0.45_0.22_350)]",
  "Gang Sheet": "bg-[oklch(0.95_0.08_300)] text-[oklch(0.45_0.2_300)]",
  "Queued for Print": "bg-[oklch(0.95_0.08_200)] text-[oklch(0.4_0.18_220)]",
  Printing: "bg-[oklch(0.92_0.14_200)] text-[oklch(0.32_0.16_220)]",
  Curing: "bg-[oklch(0.95_0.14_60)] text-[oklch(0.45_0.18_55)]",
  "Quality Check": "bg-[oklch(0.94_0.12_95)] text-[oklch(0.4_0.16_85)]",
  "Ready for Dispatch": "bg-[oklch(0.92_0.14_150)] text-[oklch(0.35_0.16_150)]",
  Completed: "bg-[oklch(0.92_0.1_160)] text-[oklch(0.32_0.14_160)]",
  "On Hold": "bg-[oklch(0.93_0.04_30)] text-[oklch(0.4_0.12_30)]",
};

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string | null;
  data: Record<string, any>;
  read_at: string | null;
  created_at: string;
  updated_at: string;
  is_read: boolean;
  expires_at: string | null;
  priority: number | null;
  included_in_digest: boolean;
}

export interface NotificationPreference {
  id: string;
  user_id: string;
  category: string;
  urgency: string;
  digest_frequency: string;
  delivery_method: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
  // Per-event, per-channel flags
  email_job_created: boolean;
  email_job_print_started: boolean;
  email_job_ready: boolean;
  email_job_on_hold: boolean;
  inapp_job_created: boolean;
  inapp_job_print_started: boolean;
  inapp_job_ready: boolean;
  inapp_job_on_hold: boolean;
  email_gang_sheet_updated: boolean;
  inapp_gang_sheet_updated: boolean;
}

export interface EmailSettings {
  id: number;
  from_name: string | null;
  from_email: string | null;
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_username: string | null;
  smtp_password: string | null;
  smtp_use_tls: boolean;
  smtp_enabled: boolean;
  updated_at: string;
}

export interface EmailTemplate {
  key: string;
  subject: string;
  body: string;
  updated_at: string;
}
